import React from 'react';
import { Helmet } from 'react-helmet-async';
import { RotateCcw, Package, AlertCircle, CreditCard, Mail } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const ReturnsPage = () => {
  return (
    <div className="min-h-screen bg-bone-white">
      <Helmet>
        <title>Returns & Refunds | The Perfect Peach Club</title>
        <meta name="description" content="Returns and refunds policy for The Perfect Peach Club. Learn about our return process for unopened items and refund timelines." />
      </Helmet>

      <Navigation />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-playfair text-4xl sm:text-5xl font-semibold text-[#2D2420] mb-4">
            Returns & Refunds
          </h1>
          <p className="text-lg text-african-earth leading-relaxed">
            At The Perfect Peach Club, your comfort and confidence matter to us.
            Because our products are intimate wellness items, we take extra care with hygiene and safety.
            For this reason, we're unable to accept returns on opened or used products.
          </p>
        </div>

        <div className="space-y-10">
          {/* Returns */}
          <section className="bg-white rounded-2xl p-6 sm:p-8 shadow-soft">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-perfect-peach/10 rounded-full flex items-center justify-center flex-shrink-0">
                <RotateCcw className="w-5 h-5 text-perfect-peach" />
              </div>
              <div>
                <h2 className="font-playfair text-2xl font-semibold text-[#2D2420] mb-4">
                  Returns
                </h2>
                <p className="text-african-earth mb-4">We gladly accept returns on:</p>
                <ul className="space-y-2 text-african-earth mb-6">
                  <li className="flex items-center gap-2">
                    <span className="text-perfect-peach">✓</span>
                    Unopened items
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-perfect-peach">✓</span>
                    Unused products
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-perfect-peach">✓</span>
                    Original packaging intact
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-perfect-peach">✓</span>
                    Within 7 days of delivery
                  </li>
                </ul>
                <p className="text-african-earth">
                  To request a return, email us at{' '}
                  <a 
                    href="mailto:info@theperfectpeachclub.co.za" 
                    className="text-perfect-peach hover:underline"
                  >
                    info@theperfectpeachclub.co.za
                  </a>
                </p>
              </div>
            </div>
          </section>

          {/* Non-returnable items */}
          <section className="bg-white rounded-2xl p-6 sm:p-8 shadow-soft">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-[#FDF2F2] rounded-full flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-[#C9A0A0]" />
              </div>
              <div>
                <h2 className="font-playfair text-2xl font-semibold text-[#2D2420] mb-4">
                  Non-returnable Items
                </h2>
                <p className="text-african-earth mb-4">For hygiene and safety reasons, we cannot accept returns on:</p>
                <ul className="space-y-2 text-african-earth mb-6">
                  <li className="flex items-center gap-2">
                    <span className="text-[#C9A0A0]">✗</span>
                    Opened products
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-[#C9A0A0]">✗</span>
                    Used items
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-[#C9A0A0]">✗</span>
                    Intimate or personal care products once unsealed
                  </li>
                </ul>
                <p className="text-african-earth bg-bone-white p-4 rounded-xl">
                  If your order arrives damaged or incorrect, please contact us right away — we'll always make it right.
                </p>
              </div>
            </div>
          </section>

          {/* Refunds */}
          <section className="bg-white rounded-2xl p-6 sm:p-8 shadow-soft">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-perfect-peach/10 rounded-full flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-5 h-5 text-perfect-peach" />
              </div>
              <div>
                <h2 className="font-playfair text-2xl font-semibold text-[#2D2420] mb-3">
                  Refunds
                </h2>
                <p className="text-african-earth leading-relaxed">
                  Once your return is received and inspected, your refund will be processed
                  to your original payment method within 3–5 business days.
                </p>
              </div>
            </div>
          </section>

          {/* Need help */}
          <section className="bg-bone-white border border-peach-light/30 rounded-2xl p-6 sm:p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 bg-perfect-peach/10 rounded-full flex items-center justify-center">
                <Mail className="w-6 h-6 text-perfect-peach" />
              </div>
            </div>
            <h2 className="font-playfair text-2xl font-semibold text-[#2D2420] mb-3">
              Need Help?
            </h2>
            <p className="text-african-earth">
              We're always here for you. Reach out anytime at{' '}
              <a 
                href="mailto:info@theperfectpeachclub.co.za" 
                className="text-perfect-peach hover:underline"
              >
                info@theperfectpeachclub.co.za
              </a>
            </p>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ReturnsPage;
